/**
 * Avoidable Utilization (Screen K) — the MOCK holding state for the #1 PACE value metric and the
 * program's central blind spot (blocked on ADT / 837I). A reusable ParticipantPicker selects a member
 * (defaulting to the first roster member); the screen binds GET /participants/:key/utilization, which
 * serves 451 SYNTHETIC rows. H3: we DELIBERATELY do NOT render those fabricated patient-level events —
 * synthetic facility_npi + primary_diagnosis_code on the #1 PACE metric is a dangerous false NON-ZERO.
 * Instead, like Care-Gaps, the body is an honest DEFERRED/MOCK holding state: a count/summary + an
 * "Awaiting ADT / 837I feed" line + the MOCK ProvenanceBadge — NO per-patient rows, NO diagnosis codes,
 * NO facility NPIs. The badge still resolves to the amber MOCK chip from meta.provenance ("Mock until:
 * adt"). The page + route are unchanged; only the body swaps to the holding state. Rows are never
 * console.logged; loading and error states are present.
 */
import { useState } from "react";
import { SkyCard, SkyLoader, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import type { UtilizationEvent } from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { utilizationListSchema } from "../lib/schemas/member";
import { useExemplarMember } from "../lib/useExemplarMember";
import { ParticipantPicker } from "../components/ParticipantPicker";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

export function Utilization() {
  // Default to the conditions-richest REAL member (utilization is a mock holding state with no exemplar
  // table; a real, clinically-rich member is the most useful default) so the picker is seeded without a
  // manual pick. The ParticipantPicker still lets the user change member.
  const [picked, setPicked] = useState<string | null>(null);
  const { key: defaultKey, error: exemplarError } = useExemplarMember("conditions");
  const key = picked ?? defaultKey;

  const { data, meta, loading, error, retry } = usePage<UtilizationEvent[]>(
    key ? `/api/v1/participants/${key}/utilization` : null,
    utilizationListSchema,
  );

  return (
    <>
      <PageHeader
        crumbs={[
          { key: "home", label: "skyCare" },
          { key: "utilization", label: "Avoidable Utilization" },
        ]}
        title="Avoidable Utilization"
        description="Acute ED / inpatient / SNF utilization, the #1 PACE value metric. There is no live ADT / 837I feed yet, so this surface is a mock holding state: the rows are synthetic placeholders, never a confirmed count of zero events."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <SkyMessageBar
          intent="warning"
          title="Mock holding state, pending the ADT / 837I feed"
          message="Avoidable utilization depends on an admit-discharge-transfer (ADT) or 837I claims feed that has not landed. The events below are synthetic placeholders. This is a named-feed dependency, not a real count, and must never be read as zero ED visits."
        />
        <div className="flex items-end justify-between gap-4">
          <div className="w-80">
            <ParticipantPicker value={key ?? undefined} onSelect={setPicked} />
          </div>
          {key && <ProvenanceBadge meta={meta?.provenance} />}
        </div>
        {/* The default-member lookup failed and the user has not picked one yet: honest bar, not a blank. */}
        {exemplarError && !key && (
          <SkyMessageBar
            intent="warning"
            title="Could not load a default member"
            message="Could not load a default member - search above to choose a participant."
          />
        )}
        {loading && <SkyLoader label="Loading utilization" />}
        {error && <PageError title="Could not load utilization" message={error} onRetry={retry} />}
        {key && !loading && !error && (
          <SkyCard
            header={{
              title: "Acute utilization",
              description: "ED / inpatient / SNF events (holding state)",
              action: <ProvenanceBadge meta={meta?.provenance} />,
            }}
          >
            {/* H3: count/summary ONLY. We never render the synthetic per-patient events (facility NPIs,
                diagnosis codes) — that would be a dangerous false NON-ZERO on the #1 PACE metric. */}
            <div className="flex flex-col gap-2">
              <SkyText size={500} weight="bold">
                Awaiting ADT / 837I feed
              </SkyText>
              <SkyText size={300}>
                {`${(data as UtilizationEvent[] | null)?.length ?? 0} synthetic placeholder event(s) are present behind the contract but are withheld from display until a live admit-discharge-transfer (ADT) or 837I claims feed lands. This is a named-feed dependency, never a confirmed count of zero events.`}
              </SkyText>
            </div>
          </SkyCard>
        )}
      </section>
      <PageFooter />
    </>
  );
}
